import { memo } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus, } from "react-syntax-highlighter/dist/cjs/styles/prism";

// Stable plugin / component identity — recreating these objects on every
// render forces react-markdown to remount the whole tree.
const REMARK_PLUGINS = [remarkGfm];
const REHYPE_PLUGINS = [rehypeRaw];

const codeComponent = ({ streaming }) => function Code({ node, inline, className, children: codeChildren, ...props }) {
    const match = /language-(\w+)/.exec(className || "");

    // During live streaming, skip Prism — re-highlighting a growing fence
    // every frame is expensive and flickers. Plain <pre><code> is enough
    // until the turn settles.
    if (!inline && match) {
        if (streaming) {
            return (
                <pre className="agents-md__pre-stream">
                    <code className={className} {...props}>
                        {codeChildren}
                    </code>
                </pre>
            );
        }
        return (
            <SyntaxHighlighter
                style={vscDarkPlus}
                customStyle={{ padding: '1rem' }}
                PreTag="div"
                language={match[1]}
                {...props}
            >
                {codeChildren}
            </SyntaxHighlighter>
        );
    }
    return (
        <code className={className} {...props}>
            {codeChildren}
        </code>
    );
};

const MARKDOWN_COMPONENTS = {
    code: codeComponent({ streaming: false }),
    h2({ node, children, ...props }) {
        if (!children) return;
        return (
            <p
                style={{ fontSize: '1.4rem', fontWeight: 600 }}
                {...props}
            >
                {children}
            </p>
        );
    },
    h1({ node, children, ...props }) {
        if (!children) return;
        return (
            <p
                style={{ fontSize: '1.6rem', fontWeight: 700 }}
                {...props}
            >
                {children}
            </p>
        );
    },
    p({ node, children, ...props }) {
        if (!children) return;
        return (
            <p
                style={{ padding: '.2rem 0rem' }}
                {...props}
            >
                {children}
            </p>
        );
    },
    a({ node, children, ...props }) {
        if (!children) return;
        return (
            <a
                target="_blank"
                {...props}
            >
                {children}
            </a>
        );
    },
    ul({ node, children, ...props }) {
        if (!children) return;
        return (
            <ul
                style={{ padding: '.6rem 0rem' }}
                {...props}
            >
                {children}
            </ul>
        );
    },
    ol({ node, children, ...props }) {
        if (!children) return;
        return (
            <ol
                style={{ padding: '.6rem 0rem' }}
                {...props}
            >
                {children}
            </ol>
        );
    },
    li({ node, children, ...props }) {
        if (!children) return;
        return (
            <li
                style={{ padding: '0rem 0rem', display: 'block', width: '100%' }}
                {...props}
            >
                {children}
            </li>
        );
    },
    table({ node, children, ...props }) {
        return (
            <div style={{
                border: '1px solid var(--lighterGrey)',
                borderRadius: '.4rem',
                overflow: 'auto',
                margin: '.4em 0',
            }}>
                <table
                    style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        borderStyle: 'hidden',
                        fontSize: 'inherit',
                    }}
                    {...props}
                >
                    {children}
                </table>
            </div>
        );
    },
    thead({ node, children, ...props }) {
        return (
            <thead {...props}>
                {children}
            </thead>
        );
    },
    th({ node, children, ...props }) {
        return (
            <th
                style={{
                    backgroundColor: 'var(--lighterGrey)',
                    padding: '.45rem .6rem',
                    textAlign: 'left',
                    border: '1px solid var(--lightGrey)',
                    fontSize: '.8em',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                }}
                {...props}
            >
                {children}
            </th>
        );
    },
    td({ node, children, ...props }) {
        return (
            <td
                style={{
                    padding: '.45rem .6rem',
                    textAlign: 'left',
                    border: '1px solid var(--lighterGrey)',
                    verticalAlign: 'top',
                    wordBreak: 'normal',
                }}
                {...props}
            >
                {children}
            </td>
        );
    },
    tr({ node, children, ...props }) {
        return (
            <tr {...props}>
                {children}
            </tr>
        );
    },
    hr() {
        return <br />;
    },
};

const STREAMING_MARKDOWN_COMPONENTS = {
    ...MARKDOWN_COMPONENTS,
    code: codeComponent({ streaming: true }),
};

function MarkdownRenderer({ children, streaming = false }) {
    return (
        <Markdown
            remarkPlugins={REMARK_PLUGINS}
            rehypePlugins={REHYPE_PLUGINS}
            components={streaming ? STREAMING_MARKDOWN_COMPONENTS : MARKDOWN_COMPONENTS}
        >
            {children}
        </Markdown>
    );
}

export default memo(MarkdownRenderer);